import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import { useCallback, useEffect, useState } from "react";
import logo from "@/staticfiles/logo-no-border.png";
import ReportReservations from "@/lib/organisms/reportReservations";
import ReportExpenses from "@/lib/organisms/ReportExpenses";
import ReportDistributionStats from "@/lib/organisms/reportSummary";
import ReportGraphs from "@/lib/organisms/reportGraphs";
import { Apartment, ApartmentStats, Convert, OwnerAirbnbReview } from "@/models/responses";
import { toPeriodName } from "@/utils/periods";
import { PeriodName } from "@/models";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { DownloadIcon } from "@radix-ui/react-icons";
import { Card } from "@/lib/shadcn/ui/card";
import { useAllApartments } from "@/store/slices/apartments";
import { Button } from "@/lib/shadcn/ui/button";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { formatDateToISODate } from "@/utils/dates";
import { usePeriodDates } from "@/hooks/usePeriodDates";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { Loader2 } from "lucide-react";

const ReportsPage = () => {
    const { periodOptions, yearOptions, ownerOptions, selectedPeriod, selectedYear, selectedOwner, ownerIdMap, expenseCategoriesMap } = useAppData();

    const allApartments = useAllApartments();
    const periodDates = usePeriodDates(selectedYear, selectedPeriod);

    const { setSelectedPeriod, setSelectedYear, setSelectedOwner } = useUserSettingsDispatch();

    const [reportApartments, setReportApartments] = useState<Apartment[]>([]);
    const [apartmentStats, setApartmentStats] = useState<ApartmentStats[]>([]);
    const [airbnbReviews, setAirbnbReviews] = useState<OwnerAirbnbReview[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (selectedOwner !== undefined && selectedYear !== undefined && selectedPeriod !== undefined) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setIsLoading(true);
            let apartments: Apartment[] = [];
            allApartments.forEach((apartment: Apartment) => {
                if (apartment.ownerIds.includes(selectedOwner)) {
                    apartments.push(apartment);
                }
            });
            let getStatsForPeriodUrl = `${apiUrl}${getApiBasePath()}/owners/${selectedOwner}/stats?year=${selectedYear}&period=${selectedPeriod}`;
            fetch(getStatsForPeriodUrl, fetchOptions)
                .then((response) => {
                    if (response) {
                        return response.text();
                    }
                    throw new Error("Unauthorized");
                })
                .then((response) => {
                    setApartmentStats(Convert.toGetStatsForApartmentsResponse(response).apartmentStats);
                    setReportApartments(apartments);
                    setIsLoading(false);
                })
                .catch((error) => {
                    toast({
                        title: "Error",
                        description: `Failed to load report data: ${error.message}`,
                        variant: "destructive",
                    });
                    setIsLoading(false);
                });
        }
    }, [allApartments, selectedOwner, selectedYear, selectedPeriod]);

    useEffect(() => {
        if (selectedOwner === 0 && ownerOptions.length > 0) {
            setSelectedOwner(Number(ownerOptions[0].value));
        }
    }, [ownerOptions, selectedOwner, setSelectedOwner]);

    const getReviewsForPeriod = useCallback(async (periodEndDate: Date, ownerId: string) => {
        const firstDayOfNextMonth = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1);
        const reviewsUrl = `${apiUrl}${getApiBasePath()}/owners/${ownerId}/airbnb-reviews?date=${formatDateToISODate(firstDayOfNextMonth)}`;
        const response = await fetch(reviewsUrl, fetchOptions);
        if (!response.ok) {
            throw new Error("Network response was not ok");
        }
        const data = await response.text();
        return Convert.toGetOwnerAirbnbReviewsResponse(data).reviews;
    }, []);

    useEffect(() => {
        if (selectedOwner !== undefined && periodDates) {
            getReviewsForPeriod(periodDates.current.endDate, selectedOwner.toString())
                .then((reviews) => {
                    setAirbnbReviews(reviews);
                })
                .catch(() => {
                    // Reviews are supplementary — on failure the report still renders without them.
                    setAirbnbReviews([]);
                });
        }
    }, [selectedOwner, periodDates, getReviewsForPeriod]);

    const handleYearChange = (val: string) => {
        setSelectedYear(Number(val));
    };

    const handlePeriodChange = (val: string) => {
        setSelectedPeriod(val as PeriodName);
    };

    const handleOwnerChange = (val: string) => {
        setSelectedOwner(Number(val));
    };
    if (isLoading || !selectedOwner || !selectedYear || !selectedPeriod) {
        return (
            <div role="status" aria-label="Loading report" className="flex min-h-[60vh] items-center justify-center bg-muted">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="sr-only">Loading report…</span>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted overflow-x-hidden">
            {/* Header Controls - Responsive layout */}
            <div className="flex flex-col sm:flex-row sm:justify-end sm:items-end gap-2 sm:gap-4 mb-8 px-4 sm:px-6 py-4 sm:mr-16">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <SearchableSelect
                        variant="volt"
                        onChange={handlePeriodChange}
                        placeholder={"Period"}
                        selectedValue={selectedPeriod}
                        options={periodOptions}
                        className="w-full sm:w-auto"
                    />

                    <SearchableSelect
                        variant="volt"
                        placeholder={"Year"}
                        onChange={handleYearChange}
                        selectedValue={selectedYear.toString()}
                        options={yearOptions}
                        className="w-full sm:w-auto"
                    />

                    <SearchableSelect
                        variant="volt"
                        placeholder={"Owner"}
                        onChange={handleOwnerChange}
                        selectedValue={selectedOwner == 0 ? "1" : selectedOwner.toString()}
                        options={ownerOptions}
                        className="w-full sm:w-auto"
                    />

                    <Button onClick={() => window.print()} variant="default" className="flex items-center justify-center gap-2">
                        <DownloadIcon className="h-4 w-4" />
                        Download
                    </Button>
                </div>
            </div>

            {/* Printable Section - Updated to match screenshot */}
            <div id="section-to-print" className="mx-auto px-4 md:px-6 pb-8 lg:max-w-7xl print:!mx-0 print:!ml-0 print:max-w-none print:m-0 print:p-0">
                <Card className="p-4 sm:p-8 bg-card print:shadow-none print:p-0 print:w-full print:!m-0">
                    {/* Header - Responsive layout */}
                    <div className="flex flex-col sm:flex-row sm:justify-start md:justify-between sm:items-center gap-4 pb-6 border-b border-border">
                        <img src={logo} alt="Keros Hospitality Logo" className="h-20 sm:h-32 w-auto max-w-[150px] sm:max-w-none object-contain" />
                        <div className="sm:text-right">
                            <p className="text-lg sm:text-xl font-semibold text-foreground mb-2">KEROS HOSPITALITY IKE</p>
                            <div className="space-y-1 text-sm sm:text-base text-muted-foreground">
                                <p>Areos 43</p>
                                <p>Palaio Faliro, 17562, Greece</p>
                                <a href="mailto:keroshospitality@gmail.com" className="text-muted-foreground hover:text-foreground">
                                    keroshospitality@gmail.com
                                </a>
                            </div>
                        </div>
                    </div>
                    {/* Title - Responsive styling */}
                    <div className="my-8 sm:my-12 text-center">
                        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
                            {toPeriodName(selectedPeriod)} {selectedYear} Performance Report
                        </h1>
                    </div>
                    {/* Client Info - Responsive layout */}
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-8 sm:mb-12">
                        <div>
                            <p className="text-muted-foreground mb-2">Client Information</p>
                            <p className="text-foreground font-medium">{ownerIdMap.get(selectedOwner)?.fullName || ""}</p>
                        </div>
                        <div>
                            <div className="flex gap-4 text-muted-foreground">
                                <span>Date Issued:</span>
                                <span className="text-foreground">
                                    {new Date().toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>
                    {/* Report Sections - Updated styling */}
                    <div className="space-y-12">
                        <section>
                            <h2 className="text-xl font-semibold text-foreground mb-6">Performance Overview</h2>
                            <ReportGraphs
                                yearAndPeriod={{
                                    year: selectedYear,
                                    period: toPeriodName(selectedPeriod),
                                }}
                                apartments={reportApartments}
                                apartmentStats={apartmentStats}
                                airbnbReviews={airbnbReviews}
                            />
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-foreground mb-6">Reservations</h2>
                            <ReportReservations
                                yearAndPeriod={{
                                    year: selectedYear,
                                    period: toPeriodName(selectedPeriod),
                                }}
                            />
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-foreground mb-6">Expenses</h2>
                            <ReportExpenses
                                apartments={reportApartments}
                                expenseCategories={expenseCategoriesMap}
                                yearAndPeriod={{
                                    year: selectedYear,
                                    period: toPeriodName(selectedPeriod),
                                }}
                            />
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-foreground mb-6">Summary</h2>
                            <ReportDistributionStats
                                apartmentStats={apartmentStats}
                                yearAndPeriod={{
                                    year: selectedYear,
                                    period: toPeriodName(selectedPeriod),
                                }}
                                apartments={reportApartments}
                            />
                        </section>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default ReportsPage;
